import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

import { METADOC_GROUP_ID_PREFIX } from "../../MetadocConstants";
import { createFacilityByDocumentGroupId } from "../../utils/tocFilterUtils";
import {
  buildTocEntriesWithPending,
  mergePendingFacilityByDocumentGroupId,
  pendingChangesToBulkItems,
} from "./lazyDocumentGroupUpdateUtils";
import { useLazyDocumentGroupUpdates } from "./useLazyDocumentGroupUpdates";

import type { TocEntry } from "../../metadoc.types";
import type { DocumentGroup } from "services/document-group-api";
import type { DocumentGroupTag } from "services/document-group-tag-api";
import type { GroupingSource } from "services/metadoc-api";
import { useBatchUpdateDocumentGroupsMutation } from "services/document-group-api";

/** Props forwarded to MetaDocTableOfContents when lazy updates are enabled. */
export type LazyDocumentGroupTocProps = {
  onDeleteEntry: (entryId: string) => Promise<void>;
  onPendingDocumentTypeChange: (groupId: number, documentType: string) => void;
  onPendingDateChange: (groupId: number, date: string | null) => void;
  onPendingMetadataChange: (
    groupId: number,
    field: "provider" | "facility",
    value: string | null,
  ) => void;
  onCancelPendingDelete: (groupId: number) => void;
  facilityByDocumentGroupIdOverride: Map<number, string>;
  hasPendingChanges: boolean;
  pendingChangeCount: number;
  onApplyPendingChanges: () => void;
  onDiscardPendingChanges: () => void;
  isApplyingPendingChanges: boolean;
};

type UseLazyDocumentGroupUpdatesIntegrationOptions = {
  enabled: boolean;
  documentGroups: DocumentGroup[];
  documentGroupTags: DocumentGroupTag[];
  metadocId: number;
  groupingSource: GroupingSource;
  onEnsureDocumentTypeVisible: (documentType: string) => void;
  startOperation: (operation: "regenerate") => void;
  attachJob: (jobId: number) => void;
  resetOperation: () => void;
};

type UseLazyDocumentGroupUpdatesIntegrationReturn = {
  tocEntries: TocEntry[];
  facilityByDocumentGroupId: Map<number, string>;
  lazyTocProps: LazyDocumentGroupTocProps | null;
};

/**
 * Orchestrates lazy document group updates for MetaDocViewerWorkspace.
 *
 * Composes pending-change state, TOC display overlays, revert-to-original
 * handlers, and the single bulk_update apply call. When disabled, returns
 * server-backed TOC data and null lazyTocProps so the workspace can keep
 * using immediate PATCH/delete paths unchanged.
 */
export const useLazyDocumentGroupUpdatesIntegration = ({
  enabled,
  documentGroups,
  documentGroupTags,
  metadocId,
  groupingSource,
  onEnsureDocumentTypeVisible,
  startOperation,
  attachJob,
  resetOperation,
}: UseLazyDocumentGroupUpdatesIntegrationOptions): UseLazyDocumentGroupUpdatesIntegrationReturn => {
  const [batchUpdateDocumentGroups] = useBatchUpdateDocumentGroupsMutation();
  const [isApplyingPendingChanges, setIsApplyingPendingChanges] =
    useState(false);
  const {
    pendingChanges,
    pendingChangeCount,
    hasPendingChanges,
    setPendingDocumentType,
    setPendingDate,
    setPendingProvider,
    setPendingFacility,
    setPendingDelete,
    removePendingDocumentType,
    removePendingDate,
    removePendingProvider,
    removePendingFacility,
    removePendingDelete,
    discardPendingChanges,
  } = useLazyDocumentGroupUpdates();

  const baseFacilityByDocumentGroupId = useMemo(
    () => createFacilityByDocumentGroupId(documentGroups),
    [documentGroups],
  );

  const tocEntries = useMemo(
    () =>
      enabled
        ? buildTocEntriesWithPending(
            documentGroups,
            documentGroupTags,
            pendingChanges,
          )
        : buildTocEntriesWithPending(
            documentGroups,
            documentGroupTags,
            new Map(),
          ),
    [documentGroups, documentGroupTags, enabled, pendingChanges],
  );

  const facilityByDocumentGroupId = useMemo(
    () =>
      enabled
        ? mergePendingFacilityByDocumentGroupId(
            baseFacilityByDocumentGroupId,
            pendingChanges,
          )
        : baseFacilityByDocumentGroupId,
    [baseFacilityByDocumentGroupId, enabled, pendingChanges],
  );

  const handlePendingDocumentTypeChange = useCallback(
    (groupId: number, documentType: string) => {
      const originalGroup = documentGroups.find(
        (group) => group.id === groupId,
      );
      // Reverting to the server value drops the pending entry to avoid a no-op apply.
      if (originalGroup && documentType === originalGroup.document_type) {
        removePendingDocumentType(groupId);
        return;
      }
      setPendingDocumentType(groupId, documentType);
      onEnsureDocumentTypeVisible(documentType);
    },
    [
      documentGroups,
      onEnsureDocumentTypeVisible,
      removePendingDocumentType,
      setPendingDocumentType,
    ],
  );

  const handlePendingDateChange = useCallback(
    (groupId: number, date: string | null) => {
      const originalGroup = documentGroups.find(
        (group) => group.id === groupId,
      );
      const originalDate = DATE_TO_STRING(
        STRING_TO_DATE(originalGroup?.date ?? null),
        "yyyy-MM-dd",
      );
      if (date === originalDate) {
        removePendingDate(groupId);
        return;
      }
      setPendingDate(groupId, date);
    },
    [documentGroups, removePendingDate, setPendingDate],
  );

  const handlePendingMetadataChange = useCallback(
    (groupId: number, field: "provider" | "facility", value: string | null) => {
      if (field === "provider") {
        const serverValue =
          documentGroups.find((group) => group.id === groupId)?.provider ||
          null;
        if (value === serverValue) {
          removePendingProvider(groupId);
        } else {
          setPendingProvider(groupId, value);
        }
        return;
      }

      const serverValue = baseFacilityByDocumentGroupId.get(groupId) || null;
      if (value === serverValue) {
        removePendingFacility(groupId);
      } else {
        setPendingFacility(groupId, value);
      }
    },
    [
      baseFacilityByDocumentGroupId,
      documentGroups,
      removePendingFacility,
      removePendingProvider,
      setPendingFacility,
      setPendingProvider,
    ],
  );

  const handlePendingDelete = useCallback(
    async (entryId: string): Promise<void> => {
      const groupId = parseInt(
        entryId.replace(METADOC_GROUP_ID_PREFIX, ""),
        10,
      );
      if (isNaN(groupId)) {
        return;
      }

      // Second click on delete cancels the pending delete, like other lazy fields.
      const existing = pendingChanges.get(groupId);
      if (existing?.delete) {
        removePendingDelete(groupId);
      } else {
        setPendingDelete(groupId);
      }
    },
    [pendingChanges, removePendingDelete, setPendingDelete],
  );

  const handleApplyPendingChanges = useCallback(async () => {
    if (!hasPendingChanges) {
      return;
    }

    setIsApplyingPendingChanges(true);

    const items = pendingChangesToBulkItems(pendingChanges);

    try {
      const response = await batchUpdateDocumentGroups(items).unwrap();
      discardPendingChanges();
      setIsApplyingPendingChanges(false);
      if (response.background_job_id) {
        startOperation("regenerate");
        attachJob(response.background_job_id);
      } else {
        resetOperation();
      }
    } catch (error) {
      // Preserve pending state so the user can retry without re-entering edits.
      Sentry.captureException(error, {
        tags: {
          feature: "metadoc",
          action: "lazy_apply_document_groups_batch",
        },
        extra: { itemCount: items.length },
      });
      KaboodleToaster.error(
        "Failed to apply pending changes. Please try again.",
      );
      setIsApplyingPendingChanges(false);
      resetOperation();
    }
  }, [
    attachJob,
    batchUpdateDocumentGroups,
    discardPendingChanges,
    hasPendingChanges,
    pendingChanges,
    resetOperation,
    startOperation,
  ]);

  useEffect(() => {
    // Pending edits are scoped to one metadoc + grouping source; discard on
    // switch, and also when entering/leaving Analyst mode, so stale edits
    // from a prior Analyst-mode visit don't reappear after an Ops-mode detour.
    discardPendingChanges();
  }, [discardPendingChanges, enabled, groupingSource, metadocId]);

  const lazyTocProps = useMemo<LazyDocumentGroupTocProps | null>(() => {
    if (!enabled) {
      return null;
    }

    return {
      onDeleteEntry: handlePendingDelete,
      onPendingDocumentTypeChange: handlePendingDocumentTypeChange,
      onPendingDateChange: handlePendingDateChange,
      onPendingMetadataChange: handlePendingMetadataChange,
      onCancelPendingDelete: removePendingDelete,
      facilityByDocumentGroupIdOverride: facilityByDocumentGroupId,
      hasPendingChanges,
      pendingChangeCount,
      onApplyPendingChanges: () => {
        void handleApplyPendingChanges();
      },
      onDiscardPendingChanges: discardPendingChanges,
      isApplyingPendingChanges,
    };
  }, [
    discardPendingChanges,
    enabled,
    facilityByDocumentGroupId,
    handleApplyPendingChanges,
    handlePendingDateChange,
    handlePendingDelete,
    handlePendingDocumentTypeChange,
    handlePendingMetadataChange,
    hasPendingChanges,
    isApplyingPendingChanges,
    pendingChangeCount,
    removePendingDelete,
  ]);

  return {
    tocEntries,
    facilityByDocumentGroupId,
    lazyTocProps,
  };
};
